import { UseMutationOptions } from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";
import {
    IMedicalDocument,
    IMedicalRecordItem,
    UploadMedicalDocumentsVariables,
} from "../library/models/medicalRecord";
import { IPagedQueryOptions } from "../library/models/page";
import { BaseService } from "./baseService";

/**
 * A patient's record: the one list of their evaluations and uploaded documents, and the
 * documents themselves.
 *
 * Evaluations are listed here and still opened and withdrawn through `EvaluationsService` --
 * this service owns only the list and the uploaded half.
 *
 * Tenant-scoped like every other: which clinic comes from the token, never from an argument.
 */
export class MedicalRecordsService extends BaseService {
    /** The key every page of every patient's list starts with; what an upload invalidates. */
    static readonly TIMELINE_URL: string = '/medical-records';

    /**
     * One patient's record, newest first by the date on each line, a page at a time.
     *
     * Paged on the server, across both kinds at once: a record grows for as long as the
     * patient is treated, and two lists fetched whole and merged here would be exactly the
     * fetch-everything this app does not do.
     */
    getTimeline(patientId: number, size: number): IPagedQueryOptions<IMedicalRecordItem> {
        return this.createGetInfiniteQuery<IMedicalRecordItem>(
            MedicalRecordsService.TIMELINE_URL, size, { patientId });
    }

    /**
     * Uploads one or more documents in a single request, all or nothing.
     *
     * Multipart: the details as a JSON `request` part, and one `files` part per file in the
     * order of the titles. The two gotchas are `InvoicingService.emitInvoiceMutation`'s --
     * `Content-Type: undefined` so the browser writes its own boundary, and the JSON wrapped
     * in a typed Blob so the server binds it as an object rather than reading a string.
     */
    uploadDocumentsMutation(): UseMutationOptions<IMedicalDocument[], Error, UploadMedicalDocumentsVariables> {
        return {
            mutationFn: (variables: UploadMedicalDocumentsVariables): Promise<IMedicalDocument[]> => {
                const body: FormData = new FormData();

                body.append(
                    'request',
                    new Blob([JSON.stringify(variables.request)], { type: 'application/json' })
                );
                variables.files.forEach((file: File) => body.append('files', file));

                return this.post<IMedicalDocument[]>('/medical-documents', body, {
                    headers: { 'Content-Type': undefined },
                    onUploadProgress: (event: AxiosProgressEvent) => {
                        if (variables.onProgress && event.total) {
                            variables.onProgress(event.loaded / event.total);
                        }
                    },
                });
            },
        };
    }

    /**
     * Withdraws a document. Soft on the server: it leaves the record and can no longer be
     * opened, and the file is kept. The API decides who may -- an administrator, or whoever
     * uploaded it for a day -- and answers a refusal in words.
     *
     * Hand-written rather than createMutation(url, 'delete'), which sends the id as `?id=`.
     */
    deleteDocumentMutation(): UseMutationOptions<void, Error, number> {
        return {
            mutationFn: (documentId: number): Promise<void> =>
                this.delete<void>(`/medical-documents/${documentId}`),
        };
    }

    /**
     * The file, as a blob. Binary, so it needs responseType 'blob' -- without it axios
     * parses the bytes as text and the file is corrupt by the time anything can show it.
     */
    async fetchDocumentFile(documentId: number): Promise<Blob> {
        return this.get<Blob>(`/medical-documents/${documentId}/file`, { responseType: 'blob' });
    }
}
